import { apiClient } from "@/lib/api";
import { filterParams, filtersOf, type FilterSearch } from "@/lib/columnFilters";
import type {
  PaginatedResponse,
  Party,
  DossierListItem,
  DossierDetail,
  DossierStatus,
  DocumentListItem,
  ExternalOrder,
  ExternalOrderSnapshot,
  AuditEvent,
} from "@/lib/types";
import { listLimit } from "@/lib/pageSize";

export interface DossiersSearch extends FilterSearch {
  /** Rows per page: 25, 50, 100 or 200 (lib/pageSize.ts). */
  limit?: string;
  q?: string;
  partyId?: string;
  status?: string;
  cursor?: string;
  sort?: string;
  order?: string;
}

export interface CreateDossierInput {
  partyId: string;
  primaryReference: string;
  description?: string;
}

export interface UpdateDossierInput {
  status?: DossierStatus;
  primaryReference?: string;
  description?: string | null;
}

export function listDossiers(
  search: DossiersSearch,
): Promise<PaginatedResponse<DossierListItem>> {
  return apiClient.get<PaginatedResponse<DossierListItem>>("/api/v1/dossiers", {
    ...(search.q ? { q: search.q } : {}),
    ...(search.partyId ? { partyId: search.partyId } : {}),
    ...(search.status ? { status: search.status } : {}),
    ...(search.cursor ? { cursor: search.cursor } : {}),
    ...(search.sort ? { sort: search.sort } : {}),
    ...(search.order ? { order: search.order } : {}),
    ...filterParams(filtersOf(search)),
    limit: listLimit(search, "dossiers"),
  });
}

// Active parties somebody approved, name-sorted — used by the list filter and the drawers. One
// from AFAS that waits for approval gets no dossier (S54).
export function listActiveParties(): Promise<Pick<Party, "id" | "name">[]> {
  return apiClient
    .get<PaginatedResponse<Party>>("/api/v1/parties", {
      isActive: "true",
      "filter.approval": "approved",
      limit: "200",
      sort: "name",
      order: "asc",
    })
    .then((r) => r.data.map((o) => ({ id: o.id, name: o.name })));
}

// The target system's own answer for one order. Fetched only when someone opens
// the order, so the dossier page never carries a payload per row.
export function getExternalOrderSnapshot(orderId: string): Promise<ExternalOrderSnapshot> {
  return apiClient.get<ExternalOrderSnapshot>(`/api/v1/external-orders/${orderId}/snapshot`);
}

export function getDossier(id: string): Promise<DossierDetail> {
  return apiClient.get<DossierDetail>(`/api/v1/dossiers/${id}`);
}

export function listDossierDocuments(id: string): Promise<DocumentListItem[]> {
  return apiClient
    .get<PaginatedResponse<DocumentListItem>>(`/api/v1/dossiers/${id}/documents`)
    .then((r) => r.data);
}

/** An order of WLX itself that the dossier holds (docs/partner-portal.md §16): transport, outbound or inbound. */
export interface WlxOrder {
  /** warehouse_*: a customer's warehouse order for a warehouse in Scoretrace (Transport → Magazijnorders). */
  kind: "transport" | "outbound" | "inbound" | "warehouse_outbound" | "warehouse_inbound";
  id: string;
  number: string;
  /** transport: blocked | accepted | rejected | cancelled; outbound and inbound: their state. */
  status: string;
  customerReference: string | null;
  consignments: number | null;
  /** A transport order's consignments, to put a CMR of one of them into the dossier. */
  consignmentRefs?: { id: string; number: string }[];
  warehouse: string | null;
  /** outbound: the load day; inbound: when it is expected. */
  date: string | null;
  createdAt: string;
}

export function listDossierWlxOrders(id: string): Promise<WlxOrder[]> {
  return apiClient.get<{ orders: WlxOrder[] }>(`/api/v1/dossiers/${id}/wlx-orders`).then((r) => r.orders);
}

export function listDossierExternalOrders(id: string): Promise<ExternalOrder[]> {
  return apiClient
    .get<PaginatedResponse<ExternalOrder>>(`/api/v1/dossiers/${id}/external-orders`)
    .then((r) => r.data);
}

// The Log tab shows a count next to the tab name, so it has to know whether the
// page it got is the whole log. Ask for the server maximum and keep hasNextPage
// rather than reporting a page size as if it were the total.
export const AUDIT_PAGE_SIZE = 200;

export function listDossierAuditEvents(
  id: string,
): Promise<{ events: AuditEvent[]; truncated: boolean }> {
  return apiClient
    .get<PaginatedResponse<AuditEvent>>(
      `/api/v1/dossiers/${id}/audit-events?limit=${AUDIT_PAGE_SIZE}`,
    )
    .then((r) => ({ events: r.data, truncated: r.pagination.hasNextPage }));
}

// RBAC (write roles) is enforced server-side.
export function createDossier(
  input: CreateDossierInput,
): Promise<DossierListItem> {
  return apiClient.post<DossierListItem>("/api/v1/dossiers", input);
}

export function updateDossier(
  id: string,
  input: UpdateDossierInput,
): Promise<DossierListItem> {
  return apiClient.patch<DossierListItem>(`/api/v1/dossiers/${id}`, input);
}

export const dossiersKeys = {
  list: (search: DossiersSearch) => ["dossiers", search] as const,
  activeParties: ["dossiers", "active-parties"] as const,
  detail: (id: string) => ["dossier", id] as const,
  documents: (id: string) => ["dossier", id, "documents"] as const,
  externalOrders: (id: string) => ["dossier", id, "external-orders"] as const,
  wlxOrders: (id: string) => ["dossier", id, "wlx-orders"] as const,
  auditEvents: (id: string) => ["dossier", id, "audit-events"] as const,
};
